import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import styles from "./styles.module.css";

const LABEL_MEDIDA_JUDICIAL = {
  desnecessidade: "Desnecessidade",
  sugestao: "Sugestão",
};

const TabelaBaseLegal = ({
  linhas = [],
  basesLegais = [],
  loading = false,
  isBloqueada = false, // Prop adicionada para consistência (tabela é somente leitura)
}) => {
  const formatarMoeda = (valor = 0) => {
    const num = typeof valor === "number" ? valor : parseFloat(valor) || 0;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(num);
  };

  // Buscar descrição da base legal e baseLegalCredito
  // Implementa mapeamento automático: quando a conta contábil (descricao) for "Depreciação",
  // busca automaticamente o baseLegalCredito correspondente da tabela plan_trib_base_legal
  const obterDadosBaseLegal = (baseLegalId) => {
    // Buscar base legal pelo ID
    let baseLegal = basesLegais.find((b) => b.id === baseLegalId || b.id_base_legal === baseLegalId);

    // Se encontrou, retornar com baseLegalCredito
    if (baseLegal) {
      const descricao = baseLegal.descricao || `Base Legal #${baseLegalId}`;
      const baseLegalCredito = baseLegal.baseLegalCredito || baseLegal.base_legal_credito || null;
      const fundamentacao = baseLegal.fundamentacao || null;
      const medidaJudicial = baseLegal.medidaJudicial || baseLegal.medida_judicial || null;

      return {
        descricao: descricao,
        baseLegalCredito: baseLegalCredito,
        fundamentacao: fundamentacao,
        medidaJudicial: medidaJudicial,
      };
    }

    // Se não encontrou pelo ID, retornar valores padrão
    return {
      descricao: `Base Legal #${baseLegalId}`,
      baseLegalCredito: null,
      fundamentacao: null,
      medidaJudicial: null,
    };
  };

  // Função auxiliar para buscar baseLegalCredito pela conta contábil (descricao)
  // Exemplo: se descricao = "Depreciação", busca o baseLegalCredito correspondente
  const buscarBaseLegalCreditoPorDescricao = (descricaoConta) => {
    if (!descricaoConta) return null;
    
    // Normalizar descrição para busca (case-insensitive, remover espaços)
    const descricaoNormalizada = descricaoConta.toLowerCase().trim();
    
    // Buscar base legal que corresponde à conta contábil
    const baseLegal = basesLegais.find((b) => {
      const descBaseLegal = (b.descricao || '').toLowerCase().trim();
      return descBaseLegal === descricaoNormalizada;
    });
    
    if (baseLegal) {
      return baseLegal.baseLegalCredito || baseLegal.base_legal_credito || null;
    }
    
    return null;
  };

  if (loading && linhas.length === 0) {
    return (
      <div className={styles.tabelaContainer}>
        <TableContainer component={Paper} className={styles.tableWrapper}>
          <Table size="small">
            <TableHead>
              <TableRow className={styles.tableHeader}>
                <TableCell>Conta Contábil</TableCell>
                <TableCell align="right">Valor Total</TableCell>
                <TableCell align="right">PIS</TableCell>
                <TableCell align="right">COFINS</TableCell>
                <TableCell>Base Legal de Crédito</TableCell>
                <TableCell>Fundamentação</TableCell>
                <TableCell>Medida Judicial</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={7} align="center" className={styles.emptyState}>
                  Carregando...
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  }

  return (
    <div className={styles.tabelaContainer}>
      <TableContainer component={Paper} className={styles.tableWrapper}>
        <Table size="small">
          <TableHead>
            <TableRow className={styles.tableHeader}>
              <TableCell className={styles.colContaContabil}>Conta Contábil</TableCell>
              <TableCell align="right" className={styles.colValorTotal}>Valor Total</TableCell>
              <TableCell align="right" className={styles.colPis}>PIS</TableCell>
              <TableCell align="right" className={styles.colCofins}>COFINS</TableCell>
              <TableCell className={styles.colBaseLegalCredito}>Base Legal de Crédito</TableCell>
              <TableCell className={styles.colFundamentacao}>Fundamentação</TableCell>
              <TableCell className={styles.colMedidaJudicial}>Medida Judicial</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" className={styles.emptyState}>
                  Nenhum registro encontrado. Certifique-se de que há registros com "Compõe Projeto" marcado e Base Legal preenchida.
                </TableCell>
              </TableRow>
            )}
            {linhas.map((linha, index) => {
              const {
                descricao: descricaoBaseLegal,
                baseLegalCredito: baseLegalCreditoPorId,
                fundamentacao,
                medidaJudicial,
              } = obterDadosBaseLegal(linha.baseLegalId);
              
              // Se não encontrou baseLegalCredito pelo ID, tentar buscar pela descricao (conta contábil)
              // Exemplo: se descricaoBaseLegal = "Depreciação", busca o baseLegalCredito correspondente
              let baseLegalCredito = baseLegalCreditoPorId;
              
              if (!baseLegalCredito && descricaoBaseLegal) {
                baseLegalCredito = buscarBaseLegalCreditoPorDescricao(descricaoBaseLegal);
              }
              
              return (
                <TableRow key={`${linha.baseLegalId}-${index}`} className={styles.tableRow}>
                  <TableCell className={styles.colContaContabil}>
                    {descricaoBaseLegal}
                  </TableCell>
                  <TableCell align="right" className={styles.colValorTotal}>
                    {formatarMoeda(linha.valorTotalEcd)}
                  </TableCell>
                  <TableCell align="right" className={styles.colPis}>
                    {formatarMoeda(linha.pis)}
                  </TableCell>
                  <TableCell align="right" className={styles.colCofins}>
                    {formatarMoeda(linha.cofins)}
                  </TableCell>
                  <TableCell className={styles.colBaseLegalCredito}>
                    {baseLegalCredito || "-"}
                  </TableCell>
                  <TableCell className={styles.colFundamentacao}>
                    {fundamentacao || "-"}
                  </TableCell>
                  <TableCell className={styles.colMedidaJudicial}>
                    {medidaJudicial ? (
                      <Chip
                        label={LABEL_MEDIDA_JUDICIAL[medidaJudicial] || medidaJudicial}
                        size="small"
                        color={medidaJudicial === "sugestao" ? "primary" : "default"}
                      />
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default TabelaBaseLegal;

